/**
 * Componente Users que permite llevar a cabo las operaciones de los usuarios
 */

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useFirestore } from 'react-redux-firebase';
import { Input, Select, Table, Spin, Button, Form, Modal, Divider, Typography, Checkbox, Alert, notification } from 'antd';
import { SearchOutlined } from '@ant-design/icons';

import { users } from '../../actions/database/users';
import { rankOf } from '../../lib/rank';
import { customers as customersActions } from '../../actions/database/customers';
import { diffLinks, linkedCustomerIds } from '../../lib/customer_links';
import { SIN_TOKEN_KINDS, CARPETAS_KINDS } from '../../lib/user_scope';
import { roleGivesCarpetas } from '../../lib/closed_folder_perms';
import { CarpetasField } from './carpetas_field';
import { USERS } from '../../../application/types';
import { filterAssignableRoles } from './roleSelect';
import { matchesSearch } from '../../lib/text_search';
import { memberEntry } from '../../lib/customer_links';
import { companyRows } from '../../lib/user_company_list';
import { loadTargetRole } from '../../lib/role_mirror';
import { CompanyPermissionsList } from './company_permissions';
import { CustomerPermissionsDrawer } from '../customers/permissions_drawer';

/**
 * Exporta el componente Search
 */
export const Users = () => {

  // Funcion que permite ejecutar todas las acciones del estado de redux
  const dispatch = useDispatch();
  // Selecciona los reductores
  const roles = useSelector(state => state.get('roles').get('roles').roles);
  const read = useSelector(state => state.get('users').get('read'));

  // Manejo de los estados del componente
  const [loading, setLoading] = useState(false);
  const [visible, setVisible] = useState(false);
  // Búsqueda en vivo (client-side): campo + texto
  const [searchField, setSearchField] = useState('all');
  const [searchText, setSearchText] = useState('');
  const [update, setUpdate] = useState(false);
  const [row, setRow] = useState({ key: [], data: [] });
  const [customers, setCustomers] = useState([]);
  const [rolesList, setRolesList] = useState([]); // catálogo de roles desde Firestore
  // Usuario recién creado: el modal NO se cierra, muestra «Usuario creado» y sus empresas.
  const [created, setCreated] = useState(null);
  // Panel de permisos de UNA empresa del usuario (el mismo de Clientes).
  const PERMS_CLOSED = { open: false, customerId: null, customerName: '', user: null };
  const [permsFor, setPermsFor] = useState(PERMS_CLOSED);
  // Rol (con rango) de cada usuario objetivo, leído del espejo público usuarios_rol
  // + roles/{rol} (nunca de users/{uid}); null = ilegible, no se ajusta.
  const [targetRoles, setTargetRoles] = useState({});

  // Hook para el control de los formularios
  const [form] = Form.useForm();
  // Hook que permite utilizar las funciones de Firestore
  const firestore = useFirestore();

  /**
   * Función que se ejecuta al crear el usuario
   */
  const onOk = () => form.submit();
  
  const isRootUser = roles?.documents?.root === true;
  // Edición: el rol (y el alcance) de alguien de tu mismo rango o superior no se cambia
  // (misma jerarquía que la regla de users y la acción users.update). Rol actual ilegible: no se bloquea
  // en la UI; la acción y las reglas lo rechazan.
  const rolLocked = (() => {
    if (!update || isRootUser) return false;
    const cur = rolesList.find(x => x.id === row.data[0]?.rol);
    if (!cur) return false;
    return rankOf({ documents: { root: cur.isRoot }, rango: cur.rango }) >= rankOf(roles);
  })();

  // Lleva los vínculos a lo elegido: addUser/removeUser por diferencia
  // (spec 2026-10-05). addUser no pisa la matriz de quien ya estaba.
  const syncLinks = async ({ email, uid, links }) => {
    // Campo oculto (sin permiso de clientes) o sin valor: no hay nada que sincronizar.
    if (!roles?.customers?.create || links === undefined) return;
    const before = uid ? linkedCustomerIds(customers, uid) : [];
    const { add, remove } = diffLinks(before, links || []);
    const failed = [];
    for (const id of add) {
      try { await dispatch(customersActions.addUser({ roles: roles.customers, id, email })); }
      catch (e) { failed.push(`${customers.find(c => c.cid === id)?.name || id}: ${e?.message || 'error desconocido'}`); }
    }
    for (const id of remove) {
      try { await dispatch(customersActions.removeUser({ roles: roles.customers, id, email })); }
      catch (e) { failed.push(`${customers.find(c => c.cid === id)?.name || id}: ${e?.message || 'error desconocido'}`); }
    }
    if (failed.length) {
      notification.warning({ message: 'Vínculos incompletos', description: `No se pudo actualizar: ${failed.join('; ')}`, placement: 'topRight' });
    }
  };
  const rootFields = (values) => isRootUser ? {
    // Alcance solo se envía si el campo está montado (rol de nivel global).
    ...(values.alcance !== undefined ? { alcance: values.alcance } : {}),
    sinToken: Object.fromEntries(SIN_TOKEN_KINDS.map(k => [k, (values.sinToken || []).includes(k)])),
    carpetas: Object.fromEntries(CARPETAS_KINDS.map(k => [k, (values.carpetas || []).includes(k)])),
  } : {};

  // Rol del usuario objetivo por uid, ya leído del espejo; null si no se pudo.
  const roleOfUid = (uid) => (uid ? targetRoles[uid] || null : null);
  const openPerms = ({ row: company, uid, email }) => setPermsFor({
    open: true,
    customerId: company.cid,
    customerName: company.name,
    user: {
      email,
      reference: { id: uid },
      isAdmin: memberEntry(customers.find(c => c.cid === company.cid)?.data, uid)?.isAdmin === true,
    },
  });
  const closeModal = () => {
    form.resetFields();
    setCreated(null);
    setPermsFor(PERMS_CLOSED);
    setVisible(false);
  };

  // Carga el rol del espejo del usuario que se edita o se acaba de crear.
  const watchedUid = created?.uid || (update && visible ? row.key[0] : null);
  useEffect(() => {
    if (!watchedUid) return undefined;
    let alive = true;
    loadTargetRole({ db: firestore, uid: watchedUid })
      .then(r => r, () => null)
      .then(r => { if (alive) setTargetRoles(m => ({ ...m, [watchedUid]: r && r.id ? r : null })); });
    return () => { alive = false; };
  }, [watchedUid, firestore]);

  /**
   * Función que permite crear un usuario
   */
  const onCreate = values => {
    setLoading(true);
    setVisible(true);

    dispatch(
      users.create({
        roles: roles.users,
        user: {
          email: values.email,
          id: values.id,
          name: values.name,
          rol: values.rol,
          password: values.password,
          company: values.company,
          ...rootFields(values)
        }
      })
    ).then( async ( action ) => {

      await syncLinks({ email: values.email, uid: null, links: values.links });
      await loadCustomersAndRoles();

      // Spec 2026-10-07 §4: el modal NO se cierra. Muestra «Usuario creado» y las
      // empresas con su botón «Permisos» (la siembra por rol ya la hizo addUser).
      setCreated({ uid: action?.users?.uid || null, email: values.email, name: values.name, rol: values.rol, links: values.links || [] });
      notification.success({
        message: 'Usuario creado',
        description: `El usuario "${values.name}" fue creado y recibirá un email para configurar su contraseña.`,
        placement: 'topRight'
      });
      setLoading(false);
      setVisible(true);
      onRead();

    }).catch( error => {

      // Mensaje visible (toast) + marca el campo correspondiente
      const isEmailDup = error?.code === 'auth/email-already-in-use';
      const isInvalidEmail = error?.code === 'auth/invalid-email';
      const isWeakPass = error?.code === 'auth/weak-password';
      const fieldToMark = (isEmailDup || isInvalidEmail) ? 'email'
                        : isWeakPass ? 'password'
                        : 'rol';

      notification.error({
        message: 'No se pudo crear el usuario',
        description: error?.message || 'Ocurrió un error desconocido.',
        placement: 'topRight',
        duration: 6
      });
      form.setFields([{ name: fieldToMark, value: values[fieldToMark], errors: [ error?.message || 'Error' ] }]);
      setVisible(true);
      setLoading(false);

    })
  };

  /**
   * Función que permite actualizar la información de un usuario
   */
  const onUpdate = values => {
    setLoading(true);
    setVisible(true);
    dispatch(
      users.update({
        roles: roles.users,
        user: {
          email: values.email,
          id: values.id,
          name: values.name,
          rol: values.rol,
          password: values.password,
          company: values.company,
          ...rootFields(values)
        },
        id: row.key[0]
      })
    ).then( async () => {

      await syncLinks({ email: row.data[0]?.email, uid: row.key[0], links: values.links });
      await loadCustomersAndRoles();

      notification.success({
        message: 'Usuario actualizado',
        description: `Los cambios en "${values.name}" se guardaron correctamente.`,
        placement: 'topRight'
      });
      form.resetFields();
      setLoading(false);
      setVisible(false);
      setRow({ key: [], data: [] });
      onRead();

    }).catch( error => {

      notification.error({
        message: 'No se pudo actualizar el usuario',
        description: error?.message || 'Ocurrió un error desconocido.',
        placement: 'topRight',
        duration: 6
      });
      form.setFields([{ name: "rol", value: values.rol, errors: [ error?.message || 'Error' ] }]);
      setVisible(true);
      setLoading(false);

    })
  };

  /**
   * Función que permite eliminar un usuario (solo root). La callable
   * elimina doc + cuenta de Auth + membresías en clientes y valida
   * server-side que quien llama sea root.
   */
  const onDeleteUser = target => {
    setLoading(true);
    dispatch( users.delete({ id: target.key }) ).then( () => {
      notification.success({
        message: 'Usuario eliminado',
        description: `"${target.name}" fue eliminado junto con su cuenta de acceso y sus permisos.`,
        placement: 'topRight'
      });
      setRow({ key: [], data: [] });
      setLoading(false);
      onRead();
    }).catch( error => {
      notification.error({
        message: 'No se pudo eliminar el usuario',
        description: error?.message || 'Ocurrió un error desconocido.',
        placement: 'topRight',
        duration: 6
      });
      setLoading(false);
    });
  };

  /**
   * Función que permite consultar la lista de usuarios (siempre completa;
   * el filtrado es client-side sobre la lista ya cargada)
   */
  const onRead = () => {
    setLoading(true);
    dispatch(
      users.read({ roles: roles.users })
    ).then( () => setLoading(false) ).catch( () => setLoading(false) );
  };

  // Ejecuta la funcion de consulta de usuario una vez termina de renderizar el componente
  useEffect(() => {
    setLoading(true);
    dispatch( users.read({ roles: roles.users }) ).then( () => setLoading(false) ).catch( () => setLoading(false) );
  }, [dispatch, roles.users]);

  // Obtiene la lista de empresas y roles una vez se monta el componente.
  // (Antes esto estaba en un useState con función init, que solo se ejecuta
  // al primer render como lazy initial state — semánticamente incorrecto
  // para side effects. Cambio a useEffect.)
  const loadCustomersAndRoles = useCallback(async () => {
    try {
      const [customersSnap, rolesSnap] = await Promise.all([
        firestore.collection('customers').get(),
        firestore.collection('roles').get()
      ]);
      setCustomers(
        customersSnap.docs.map(doc => ({
          name: doc.data()?.['Razon social'],
          cid: doc.id,
          data: doc.data()
        })) || []
      );
      setRolesList(
        rolesSnap.docs.map(doc => ({
          id: doc.id,
          // Algunos roles pueden tener un campo `name` legible; si no,
          // usamos el id (ej: 'cliente', 'admin').
          label: doc.data()?.name || doc.data()?.Nombre || doc.id,
          // Marca aditiva: solo el rol root la tiene. Usada para impedir
          // que un no-root lo ofrezca en el selector (anti-escalada UI).
          isRoot: doc.data()?.documents?.root === true,
          isAll: doc.data()?.documents?.level === 'all',
          isSupervisor: doc.data()?.documents?.supervisor === true,
          // Abrir/cerrar que ya da el rol (Editar usuario los muestra bloqueados).
          carpetas: roleGivesCarpetas(doc.data()),
          rango: doc.data()?.rango
        })) || []
      );
    } catch (e) {
      console.warn('No se pudieron cargar customers/roles:', e?.message);
    }
  }, [firestore]);

  useEffect(() => { loadCustomersAndRoles(); }, [loadCustomersAndRoles]);

  // Filas de la tabla a partir del estado de redux (lista completa)
  const rows = useMemo(() => (
    read?.users?.map( user => ({
      key: user.id,
      id: user.data['Identificacion'],
      name: user.data['Nombre completo'],
      company: user.data?.['Empresa']?.data()?.['Razon social'] || 'Sin una empresa asociada',
      email: user.data['Email'],
      rol: user.data['Rol'].id,
      cid: user.data?.['Empresa']?.ref.id || undefined,
      alcance: user.data?.['Alcance'] || 'todas',
      sinToken: user.data?.['SinToken'] || {},
      carpetas: user.data?.['Carpetas'] || {}
    })) || []
  ), [read]);

  // Búsqueda en vivo: por campo puntual o por todos a la vez (incluida la
  // empresa), insensible a mayúsculas/tildes y por palabras en cualquier orden
  const filtered = useMemo(() => {
    if (!searchText.trim()) return rows;
    return rows.filter( row => {
      const haystack = searchField === 'all'
        ? `${row.id} ${row.name} ${row.email} ${row.company} ${row.rol}`
        : row[searchField];
      return matchesSearch(haystack, searchText);
    });
  }, [rows, searchText, searchField]);

  const SEARCH_PLACEHOLDERS = {
    all: 'Buscar por nombre, identificación, email, empresa o rol...',
    name: 'Buscar por nombre...',
    id: 'Buscar por identificación...',
    email: 'Buscar por email...',
    company: 'Buscar por empresa...',
    rol: 'Buscar por rol...'
  };

  /**
   * Metodo que renderiza el componente
   */
  return (
    <div>

      <div style = {{ marginBottom: 15, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }} >
        <Input
          allowClear
          value = { searchText }
          onChange = { e => setSearchText(e.target.value) }
          placeholder = { SEARCH_PLACEHOLDERS[searchField] }
          prefix = { <SearchOutlined style = {{ color: 'rgba(0,0,0,.25)' }} /> }
          style = {{ flex: 1, minWidth: 260 }}
          addonBefore = { (
            <Select
              value = { searchField }
              style = {{ width: 150 }}
              onSelect = { value => setSearchField(value) }
            >
              <Select.Option value = "all" > Todos los campos </Select.Option>
              <Select.Option value = "name" > Nombre </Select.Option>
              <Select.Option value = "id" > Identificacion </Select.Option>
              <Select.Option value = "email" > Email </Select.Option>
              <Select.Option value = "company" > Empresa </Select.Option>
              <Select.Option value = "rol" > Rol </Select.Option>
            </Select>
          )}
        />
        { searchText.trim() ?
            <Typography.Text type = "secondary" >
              { filtered.length } de { rows.length } usuarios
            </Typography.Text> : null }
      </div>

      { read?.type === USERS.READ.READED_USERS ?
          <Table
            bordered
            size = 'middle'
            loading = { loading }
            locale = {{ emptyText: 'La lista de usuarios se encuentra vacia' }}
            title = { () => (
              <>
                
                { roles?.users?.create ?
                    <Button type = 'dashed'
                      disabled = { read?.type !== USERS.READ.READED_USERS }
                      onClick = { () => {
                        form.resetFields();
                        setVisible(true);
                        setUpdate(false);
                      }}
                    > Nuevo usuario </Button> : null }

                <Divider type = "vertical" />                        

                { roles?.users?.update ?
                    <Button type = 'dashed'
                      disabled = { !row.key.length > 0 }
                      onClick = { () => {
                        setVisible(true);
                        setUpdate(true);
                        form.setFields([
                          { name: "name", value: row?.data[0]?.name },
                          { name: "id", value: row?.data[0]?.id },
                          { name: "email", value: row?.data[0]?.email },
                          { name: "password", value: 'xxxxxxxxxxxx' },
                          { name: "rol", value: row?.data[0]?.rol },
                          { name: "company", value: row?.data[0]?.cid },
                          { name: "links", value: linkedCustomerIds(customers, row?.data[0]?.key) },
                          { name: "alcance", value: row?.data[0]?.alcance || 'todas' },
                          { name: "sinToken", value: SIN_TOKEN_KINDS.filter(k => row?.data[0]?.sinToken?.[k] === true) },
                          { name: "carpetas", value: CARPETAS_KINDS.filter(k => row?.data[0]?.carpetas?.[k] === true) },
                        ]);
                      }}
                    > Editar usuario </Button> : null }

                {/* Eliminar es exclusivo de root (documents.root); la callable
                    deleteUserAccount re-valida server-side */}
                { roles?.documents?.root === true ?
                    <>
                      <Divider type = "vertical" />
                      <Button type = 'dashed' danger
                        disabled = { !row.key.length > 0 }
                        onClick = { () => {
                          const target = row?.data[0];
                          Modal.confirm({
                            title: `Eliminar usuario ${ target?.name }`,
                            content: `Se eliminará la cuenta de "${ target?.email }", su acceso al sistema y sus permisos en todas las empresas. Esta acción no se puede deshacer.`,
                            okText: 'Eliminar',
                            okType: 'danger',
                            cancelText: 'Cancelar',
                            onOk: () => onDeleteUser( target )
                          });
                        }}
                      > Eliminar usuario </Button>
                    </> : null }

              </>
            )}
            columns = {[
              { title: 'Identificacion', dataIndex: 'id', key: 'id' },
              { title: 'Nombre', dataIndex: 'name', key: 'name' },
              { title: 'Empresa', dataIndex: 'company', key: 'company' },
              { title: 'Email', dataIndex: 'email', key: 'email' },
              { title: 'Rol', dataIndex: 'rol', key: 'rol' },
              ...(isRootUser ? [{ title: 'Alcance', dataIndex: 'alcance', key: 'alcance', render: (v, r) => (v === 'empresas' || rolesList.find(x => x.id === r.rol)?.isAll === false) ? 'Sus empresas' : 'Todas' }] : []),
            ]}
            dataSource = { filtered }
            pagination = {{
              showTotal: ( total, range ) => `${range[0]}-${range[1]} de ${total} usuarios`
            }}
            rowSelection = {{
              type: 'radio',
              selectedRowKeys: row.key,
              onChange: ( key, row ) => setRow({ key, data: row }) 
            }}
          />
        : read?.type === USERS.READ.READING_USERS ? <div style = {{ textAlign: 'center' }} > <Spin /> </div> : null }

      { roles?.users?.create || roles?.users?.update ?
          <Modal
            forceRender
            // Mientras el panel de permisos está abierto, el modal se oculta (sin
            // desmontarse: conserva el formulario) para que no lo tape.
            visible = { visible && !permsFor.open }
            confirmLoading = { loading }
            title = { created ? "Usuario creado" : !update ? "Nuevo usuario" : "Editar usuario" }
            okText = { !update ? "Crear" : "Editar" }
            cancelText = "Cancelar"
            maskClosable = { false }
            closable = { false }
            footer = { created ? <Button type = 'primary' onClick = { closeModal } > Cerrar </Button> : undefined }
            onOk = { onOk }
            onCancel = { () => ( created ? closeModal() : setVisible(false) ) } >
            { created ?
              <div>
                <Alert type = "success" showIcon style = {{ marginBottom: 16 }}
                  message = "Usuario creado"
                  description = { `"${ created.name }" fue creado y recibirá un email para configurar su contraseña. Ya tiene en cada empresa vinculada los permisos de su rol; ajústelos aquí si hace falta.` } />
                <CompanyPermissionsList
                  rows = { companyRows({ links: created.links, customers, uid: created.uid, viewerRole: roles, targetRole: roleOfUid( created.uid ) }) }
                  onOpen = { company => openPerms({ row: company, uid: created.uid, email: created.email }) } />
              </div> : null }
            <div style = {{ display: created ? 'none' : 'block' }} >
            <Form form = { form } name = "users" onFinish = { !update ? onCreate : onUpdate } layout = "vertical" >
              <Form.Item label = "Nombre completo" name = "name"
                rules = {[{ required: true, message: 'El nombre es requerido' }]} >
                <Input />
              </Form.Item>
              <Form.Item label = "Identificacion" name = "id"
                rules = {[{ required: true, message: "El numero de identificacion es requerido" }]} >
                <Input />
              </Form.Item>
              <Form.Item label = "Email" name = "email"
                rules = {[
                  { required: true, message: 'La direccion de email es requerida' },
                  { type: 'email', message: 'Debe ingresar una direccion de correo valida' }
                ]} >
                <Input />
              </Form.Item>
              <Form.Item label = "Password" name = "password"
                rules = {[
                  { required: true, message: 'El password es requerido' },
                  { min: 6, message: 'El password debe tener minimo 6 caracteres' }
                ]} >
                <Input type = 'password' disabled = { update } />
              </Form.Item>
              <Form.Item label = "Rol" name = "rol"
                extra = { rolLocked ? 'No puedes cambiar el rol ni el alcance de alguien de tu mismo rango o superior.' : undefined }
                rules = {[{ required: true, message: 'El rol es requerido' }]} >
                <Select placeholder = "Seleccione un rol" showSearch optionFilterProp = "children"
                  disabled = { rolLocked } >
                  { filterAssignableRoles(rolesList, roles?.documents?.root === true).map(r => (
                    <Select.Option key = { r.id } value = { r.id }>{ r.label }</Select.Option>
                  )) }
                </Select>
              </Form.Item>
              <Form.Item label = "Empresa" name = "company"
                rules = {[{ required: true, message: "La empresa es requerida" }]} >
                <Select onChange = { (cid) => {
                  const cur = form.getFieldValue('links') || [];
                  if (!cur.includes(cid)) form.setFieldsValue({ links: [...cur, cid] });
                }} >
                  { customers.map((customer, index) => (
                    <Select.Option key = { index } value = { customer.cid } > { customer.name } </Select.Option>
                  )) }
                </Select>
              </Form.Item>
              { roles?.customers?.create ?
                <Form.Item label = "Clientes vinculados" name = "links"
                  tooltip = "Empresas donde el usuario tendrá acceso (solo lectura al vincular; los demás permisos se asignan en Permisos).">
                  <Select mode = "multiple" allowClear showSearch optionFilterProp = "children" placeholder = "Seleccione los clientes" >
                    { customers.map((customer) => (
                      <Select.Option key = { customer.cid } value = { customer.cid } > { customer.name } </Select.Option>
                    )) }
                  </Select>
                </Form.Item> : null }
              { roles?.customers?.create ?
                <Form.Item noStyle shouldUpdate = { (a, b) => a.links !== b.links } >
                  { ({ getFieldValue }) => (
                    <Form.Item label = "Permisos por empresa"
                      extra = "Abre los permisos del usuario en cada empresa elegida (los mismos de Clientes). Para el personal global sin matriz parte de los permisos del rol y, al guardar, crea la reducción de esa empresa.">
                      <CompanyPermissionsList
                        rows = { companyRows({
                          links: getFieldValue('links'),
                          customers,
                          uid: update ? row.key[0] : null,
                          viewerRole: roles,
                          targetRole: roleOfUid( update ? row.key[0] : null ),
                        }) }
                        onOpen = { company => openPerms({ row: company, uid: row.key[0], email: row.data[0]?.email }) } />
                    </Form.Item>
                  ) }
                </Form.Item> : null }
              { isRootUser ?
                <>
                  <Form.Item noStyle shouldUpdate = { (a, b) => a.rol !== b.rol } >
                    { ({ getFieldValue }) => rolesList.find(r => r.id === getFieldValue('rol'))?.isAll ?
                      <Form.Item label = "Alcance" name = "alcance" initialValue = "todas"
                        tooltip = "Limita un rol de nivel global a los clientes vinculados.">
                        <Select>
                          <Select.Option value = "todas" > Todas las empresas </Select.Option>
                          <Select.Option value = "empresas" > Solo sus empresas </Select.Option>
                        </Select>
                      </Form.Item> : null }
                  </Form.Item>
                  <Form.Item label = "Eliminar sin token" name = "sinToken"
                    tooltip = "Borra directo, sin token ni solicitud. Queda en la papelera y en auditoría.">
                    <Checkbox.Group options = {[
                      { label: 'Documentos', value: 'document' },
                      { label: 'Contabilizaciones', value: 'accounting' },
                      { label: 'Vinculados', value: 'linked' },
                      { label: 'XML', value: 'xml' },
                    ]} />
                  </Form.Item>
                  <Form.Item noStyle shouldUpdate = { (a, b) => a.rol !== b.rol } >
                    { ({ getFieldValue }) =>
                      <Form.Item label = "Abrir y cerrar carpetas" name = "carpetas"
                        tooltip = "En todas sus empresas, sumado a lo que da su rol. Los permisos por empresa pueden decidir distinto en cada empresa. Solo lo asigna root.">
                        <CarpetasField roleGives = { rolesList.find(r => r.id === getFieldValue('rol'))?.carpetas || {} } />
                      </Form.Item> }
                  </Form.Item>
                </> : null }
              {/* La verificación en 2 pasos es obligatoria para todos los
                  usuarios (política global): users.create/update siempre
                  marcan mfa.required=true, sin switch de excepción. */}
            </Form>
            </div>
          </Modal>
        : null }

      <CustomerPermissionsDrawer
        open = { permsFor.open }
        customerId = { permsFor.customerId }
        customerName = { permsFor.customerName }
        user = { permsFor.user }
        zIndex = { 1100 }
        onClose = { () => setPermsFor( PERMS_CLOSED ) } />

    </div>
  );
};